'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useWindowSize from '@alltrails/core/hooks/useWindowSize';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import useSections from '../../hooks/useSections';
import { setSections } from '../../slices/sections';
import { useAppDispatch } from '../../utils/redux';
import type { HomepageSection, LocationData } from '../../utils/types';
import AppInstallSection from '../blocks/AppInstallSection';
import CommunityStats from '../blocks/CommunityStats';
import GearShop from '../blocks/GearShop';
import GenericGiftingBlock from '../blocks/GenericGiftingBlock';
import SeoLinks, { type SeoLinks as SeoLinksData } from '../blocks/SeoLinks';
import SponsoredLists from '../blocks/SponsoredLists';
import type { HitTrail } from '../blocks/TrailCarousel';
import TrailRiverTitle from '../TrailRiverTitle';

const ActivitiesRiver = dynamic(() => import('../blocks/ActivitiesRiver'));
const ExploreAndDiscover = dynamic(() => import('../blocks/ExploreAndDiscover'));
const TrailCarousel = dynamic(() => import('../blocks/TrailCarousel'));
const UgcSocialFooter = dynamic(() => import('../blocks/UgcSocialFooter'));
const UpgradeYourAdventures = dynamic(() => import('../blocks/UpgradeYourAdventures'));

type Props = {
  sections?: HomepageSection[];
  latLng?: [number, number];
  locationData?: LocationData;
};

const BlockSwitch = ({ sections: initialSections = [], latLng, locationData }: Props) => {
  const { width } = useWindowSize();
  const languageRegionCode = useLanguageRegionCode();
  const dispatch = useAppDispatch();
  const reduxSections = useSelector((state: { sections?: { sections?: HomepageSection[] } }) => state.sections?.sections);
  const sections = reduxSections || initialSections;
  const isEnglishLocale = languageRegionCode === 'en-US' || languageRegionCode === 'en-GB';

  const experiments = useExperiments();
  const moduleRemoveExperimentVariant = experiments?.['web-growth-rm-homepage-module-removal-v2']?.value;
  const appInstallExperimentVariant = experiments?.['web-growth-rm-homepage-app-install-module']?.value;

  const sendExposure = useExposureEvent('web-growth-rm-homepage-app-install-module');

  useEffect(() => {
    if (isEnglishLocale) {
      sendExposure();
    }
  }, [sendExposure, isEnglishLocale]);

  useEffect(() => {
    if (initialSections.length > 0 && !reduxSections) {
      dispatch(setSections(initialSections));
    }
  }, [initialSections, reduxSections, dispatch]);

  const processedSections = useSections({ initialSections: sections, latLng, locationData });

  const isModuleRemovalTreatmentActive = moduleRemoveExperimentVariant === 'treatment';
  const isAppInstallTreatmentActive = appInstallExperimentVariant === 'stacked' || appInstallExperimentVariant === 'condensed';
  const showAppInstallModule = isEnglishLocale && isAppInstallTreatmentActive;

  const excludedSections = [
    ...(isModuleRemovalTreatmentActive ? ['community_stats', 'attribute', 'gifting_upsell', 'ugc_social_footer'] : []),
    ...(showAppInstallModule ? ['explore_and_discover', 'upgrade_your_adventures'] : [])
  ];
  const filteredSections = processedSections.filter(section => !excludedSections.includes(section.section_type));

  // Insert app install section as the second block
  // Translations will be added in this ticket: https://alltrails.atlassian.net/browse/GROW-15823
  const sectionsWithAppInstall: HomepageSection[] =
    showAppInstallModule && filteredSections.length > 0
      ? [filteredSections[0], { section_type: 'app_install_cta' }, ...filteredSections.slice(1)]
      : filteredSections;

  const renderSection = useCallback(
    (section: HomepageSection, idx: number) => {
      switch (section.section_type) {
        case 'attribute': {
          const activities =
            !section.objects || !Array.isArray(section.objects)
              ? []
              : (section.objects as Array<{
                  title: string;
                  filters: { activity: string[] };
                  photo_urls: { square: { two: string; three: string } };
                }>);

          const activitiesTitle = section.title || '';

          return <ActivitiesRiver activities={activities} windowWidth={width} title={activitiesTitle} />;
        }

        case 'seo_links': {
          const seoLinksData = (section.data as SeoLinksData) || [];
          if (seoLinksData.length === 0) return null;
          return <SeoLinks data={seoLinksData} />;
        }

        case 'community_stats':
          return <CommunityStats />;

        case 'sponsored_lists':
          return <SponsoredLists title={section.title} collections={section.collections} />;

        case 'gifting_upsell':
          return <GenericGiftingBlock />;

        case 'merchandise_store':
          if (languageRegionCode === 'en-US' && locationData?.country === 'US') {
            return <GearShop />;
          }
          return null;

        case 'trail_list': {
          return (
            <TrailCarousel
              hits={section.hits}
              title={<TrailRiverTitle section={section} idx={idx} locationData={locationData} languageRegionCode={languageRegionCode} />}
            />
          );
        }

        case 'googlebot_trail_list': {
          const hits = section.trails.concat({
            exploreMoreUrl: `/explore/${section.country_slug}`
          } as HitTrail);

          return <TrailCarousel hits={hits} title={section.title || ''} />;
        }

        case 'ugc_social_footer':
          return <UgcSocialFooter />;

        case 'app_install_cta':
          return <AppInstallSection />;

        case 'explore_and_discover':
          return <ExploreAndDiscover />;

        case 'upgrade_your_adventures':
          return <UpgradeYourAdventures windowWidth={width} />;

        default:
          return null;
      }
    },
    [width, languageRegionCode, locationData]
  );

  return (
    <>
      {sectionsWithAppInstall.map((section, index) => (
        <div key={`${section.section_type}-${index}`}>{renderSection(section, index)}</div>
      ))}
    </>
  );
};

export default BlockSwitch;
